/* blog-archive-calendar.js v1.13.0
 *
 * v1.13.0 — editor-only marker style preview: when the container carries
 * data-preview-dots-editor="true" (blocks/blog-archive-calendar-alert.liquid,
 * only emitted inside {% if request.design_mode %}) AND window.Shopify.
 * designMode is true, buildDotsRow() draws a deterministic made-up
 * done/started pattern per day (fakePreviewPattern()) instead of this
 * browser's/customer's real progress, so the block's new marker-styling
 * controls can be seen and tuned in the editor without needing real puzzle
 * data. Reads and writes nothing real; a real storefront visitor never sets
 * the data attribute, so this can't run for them even if the designMode
 * check were somehow bypassed.
 *
 * v1.12.0 — the "--started" ring's border width, border color, fill color,
 * and dot size are now overridable via --sdk-cal-started-* CSS variables,
 * each falling back to its original v1.11.0 hardcoded value when unset.
 * blocks/blog-archive-calendar-alert.liquid's new "In-progress marker"
 * schema section sets them; blocks/blog-archive-calendar.liquid sets none
 * of them and is unaffected.
 *
 * v1.11.0 — the "--started" dot from v1.8.0 is now styled (a 1px ring in the
 * per-level colour, no fill) and gated by its own toggle, data-started-enabled
 * (block setting show_started_dots), independent of the completion-dots toggle.
 * getStartedLevels()'s local test now also counts a pencil mark, matching the
 * Worker (v1.05.00) — a level is "in progress" while it has any player content.
 * Also: onPuzzleCompleted() now patches the page's frozen serverStarted
 * snapshot (patchServerStarted()) when a sdk:puzzle-progress event reports a
 * level's started state — "started" isn't monotonic like a completion (Clear
 * flips it back off), so the stale snapshot from page load can no longer
 * out-vote a fresh "no longer started" for the rest of this page view.
 *
 * v1.10.0 — when the container carries data-soft-swap (the in-puzzle archive-
 * calendar alert only, never the standalone page block), a click on a
 * non-gated day fires a cancelable "sdk:calendar-day-pick" event
 * {detail:{date,url}} instead of navigating. If a listener cancels it
 * (snippets/sudoku-carousel-grid.liquid, wireArchiveCalendarAlert) the
 * navigation is suppressed and the puzzle soft-swaps to that day in
 * fullscreen; if nothing handles it the link navigates normally.
 *
 * v1.9.0 — container.sdkCalGoToDate(dateStr): jump the visible month and
 * active day to an arbitrary date. Used by the in-puzzle archive-calendar
 * alert so it opens on whatever day the fullscreen puzzle currently shows
 * (which changes by soft-swap, no reload) rather than the page-load day.
 *
 * v1.8.0 — a day cell can now show a THIRD state per difficulty: started but
 * not finished. getStartedLevels() mirrors getCompletedLevels() exactly (same
 * server + local union, same single seam) and buildDotsRow() adds a
 * "--started" class. That class ships deliberately UNSTYLED: the data and the
 * hook land here, the visual treatment is a separate decision.
 *
 * v1.7.0 — publishes window.sdkArchiveAPI (which days exist, which levels are
 * completed, URL for a date) once the archive has loaded, plus an
 * "sdk:archive-ready" event. Consumed by the puzzle grid's date-swipe day
 * navigation. See the API's own comment block below for why getCompletedLevels
 * is exposed rather than duplicated.
 *
 * Month-view calendar for the Sudoku archive.
 *
 * Data is fetched client-side from the Shopify Storefront API using cursor-based
 * GraphQL pagination — no article ceiling, always reflects live published articles.
 * Results are cached in sessionStorage for the duration of the browser session
 * (instant render on article-to-article navigation within the same tab).
 *
 * Required: Theme Settings > Developer > Storefront API token (public, read-only).
 * Shop domain is read from window.Shopify.shop (present on all Shopify theme pages).
 *
 * Non-member gating: past days (not today) link to data-gate-url instead of the
 * real article when data-is-member is not "true". Today's date always links to
 * the real article, matching the site's "today is free" rule.
 *
 * Active day (data-active-date, the article currently being viewed) is never a
 * link — it's already the current page — regardless of gating/member status.
 *
 * Completion dots (paid members): server-synced data arrives pre-rendered via
 * data-server-completions (customer.metafields.custom.sudoku_completions —
 * see blocks/blog-archive-calendar.liquid) and is unioned with this browser's
 * own localStorage in getCompletedLevels() — the single seam this was built
 * around. See the shopify-app-proxy skill for the full server-side design.
 */
(function () {
  "use strict";

  var MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  var DOW_SUN = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
  var DOW_MON = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

  var GQL_QUERY =
    "query GetArticles($handle:String!,$first:Int!,$after:String){blog(handle:$handle){articles(first:$first,after:$after,sortKey:PUBLISHED_AT,reverse:true){pageInfo{hasNextPage endCursor}edges{node{publishedAt onlineStoreUrl}}}}}";

  function pad2(n) {
    return (n < 10 ? "0" : "") + n;
  }

  function toDateStr(y, m, d) {
    return y + "-" + pad2(m + 1) + "-" + pad2(d);
  }

  function fetchArchive(token, blogHandle, todayStr, callback) {
    var cacheKey = "sdk-archive-" + blogHandle;
    var cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        var parsed = JSON.parse(cached);
        if (parsed && parsed.date === todayStr) {
          callback(parsed.articles);
          return;
        }
      } catch (e) {
        /* fall through to fetch */
      }
    }

    var endpoint = window.location.origin + "/api/2026-04/graphql.json";
    var collected = [];

    function fetchPage(cursor) {
      var variables = { handle: blogHandle, first: 250 };
      if (cursor) variables.after = cursor;

      fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": token,
        },
        body: JSON.stringify({ query: GQL_QUERY, variables: variables }),
      })
        .then(function (res) {
          return res.json();
        })
        .then(function (json) {
          var articles = json.data && json.data.blog && json.data.blog.articles;
          if (!articles) {
            callback([]);
            return;
          }

          var edges = articles.edges;
          for (var i = 0; i < edges.length; i++) {
            var node = edges[i].node;
            if (!node.onlineStoreUrl) continue;
            var dateStr = new Date(node.publishedAt).toLocaleDateString(
              "sv-SE",
              { timeZone: "Europe/Vienna" },
            );
            var pathname = new URL(node.onlineStoreUrl).pathname;
            collected.push([dateStr, pathname]);
          }

          if (articles.pageInfo.hasNextPage) {
            fetchPage(articles.pageInfo.endCursor);
          } else {
            try {
              sessionStorage.setItem(
                cacheKey,
                JSON.stringify({ date: todayStr, articles: collected }),
              );
            } catch (e) {
              /* storage full — skip cache */
            }
            console.log(
              "[SDK Calendar] Storefront API — " +
                collected.length +
                ' articles loaded for "' +
                blogHandle +
                '"',
            );
            callback(collected);
          }
        })
        .catch(function () {
          callback([]);
        });
    }

    fetchPage(null);
  }

  function injectStyles() {
    if (document.getElementById("sdk-cal-css")) return;
    var s = document.createElement("style");
    s.id = "sdk-cal-css";
    s.textContent = [
      ".sdk-cal { width: 100%; }",
      ".sdk-cal-nav {",
      "  display: flex;",
      "  align-items: center;",
      "  justify-content: space-between;",
      "  margin-bottom: 12px;",
      "}",
      ".sdk-cal-nav__btn {",
      "  background: none;",
      "  border: none;",
      "  cursor: pointer;",
      "  color: var(--color-foreground);",
      "  display: flex;",
      "  align-items: center;",
      "  padding: 4px 10px;",
      "  opacity: 0.6;",
      "}",
      ".sdk-cal-nav__btn:hover { opacity: 1; }",
      ".sdk-cal-nav__btn[disabled] {",
      "  opacity: 0.18;",
      "  cursor: default;",
      "  pointer-events: none;",
      "}",
      ".sdk-cal-nav__label {",
      "  font-family: var(--font-body--family);",
      "  font-size: 0.875rem;",
      "  font-weight: 600;",
      "  letter-spacing: 0.04em;",
      "  color: var(--color-foreground);",
      "}",
      ".sdk-cal-grid {",
      "  display: grid;",
      "  grid-template-columns: repeat(7, 1fr);",
      "  gap: 2px;",
      "}",
      ".sdk-cal-dow {",
      "  text-align: center;",
      "  font-family: var(--font-body--family);",
      "  font-size: 0.7rem;",
      "  text-transform: uppercase;",
      "  letter-spacing: 0.08em;",
      "  padding: 4px 0 8px;",
      "  color: var(--color-foreground);",
      "  opacity: 0.45;",
      "}",
      ".sdk-cal-day {",
      "  position: relative;",
      "  text-align: center;",
      "}",
      /* Reserved dot strip — padding-top on EVERY day cell, only when the
         feature is on for this instance (gated on the container's own
         data-dots-enabled, not on whether this particular day has dots).
         This is what keeps grid row heights uniform: the reservation is
         identical whether or not a given cell actually renders any dots,
         so CSS Grid's row-stretch behavior never sees a height difference
         between cells. See .sdk-cal-day__dots below — it's taken out of
         flow entirely, so it never contributes to the cell's own height. */
      '.sdk-cal[data-dots-enabled="true"] .sdk-cal-day {',
      "  padding-top: calc(var(--sdk-cal-dot-size, 6px) + 6px);",
      "}",
      ".sdk-cal-day__dots {",
      "  position: absolute;",
      "  top: 0;",
      "  left: 0;",
      "  right: 0;",
      "  display: flex;",
      "  justify-content: center;",
      "  align-items: center;",
      "  gap: 2px;",
      "  padding: var(--sdk-cal-dot-pad-top, 0) var(--sdk-cal-dot-pad-right, 0) var(--sdk-cal-dot-pad-bottom, 0) var(--sdk-cal-dot-pad-left, 0);",
      "}",
      ".sdk-cal-day__dot {",
      "  width: var(--sdk-cal-dot-size, 6px);",
      "  height: var(--sdk-cal-dot-size, 6px);",
      "  border-radius: 50%;",
      "  background: transparent;",
      "  flex: 0 0 auto;",
      "}",
      ".sdk-cal-day__dot--done:nth-child(1) { background: var(--sdk-cal-dot-color-0); }",
      ".sdk-cal-day__dot--done:nth-child(2) { background: var(--sdk-cal-dot-color-1); }",
      ".sdk-cal-day__dot--done:nth-child(3) { background: var(--sdk-cal-dot-color-2); }",
      ".sdk-cal-day__dot--done:nth-child(4) { background: var(--sdk-cal-dot-color-3); }",
      ".sdk-cal-day__dot--done:nth-child(5) { background: var(--sdk-cal-dot-color-4); }",
      /* Started-but-unfinished: a ring in the same per-level colour, no fill
         by default. inset box-shadow (not border) so the dot keeps its exact
         size. Width/color/fill/size are all overridable via the
         --sdk-cal-started-* variables (2026-09-14, blocks/blog-archive-
         calendar-alert.liquid's "In-progress marker" schema section) — every
         one of them falls back to the ORIGINAL hardcoded value (1px, the
         matching completion-dot color, transparent, the shared dot size)
         when unset, so blocks/blog-archive-calendar.liquid — which sets none
         of these — renders exactly as it always has. */
      ".sdk-cal-day__dot--started {",
      "  width: var(--sdk-cal-started-dot-size, var(--sdk-cal-dot-size, 6px));",
      "  height: var(--sdk-cal-started-dot-size, var(--sdk-cal-dot-size, 6px));",
      "  background: var(--sdk-cal-started-fill-color, transparent);",
      "}",
      ".sdk-cal-day__dot--started:nth-child(1) { box-shadow: inset 0 0 0 var(--sdk-cal-started-border-width, 1px) var(--sdk-cal-started-border-color, var(--sdk-cal-dot-color-0)); }",
      ".sdk-cal-day__dot--started:nth-child(2) { box-shadow: inset 0 0 0 var(--sdk-cal-started-border-width, 1px) var(--sdk-cal-started-border-color, var(--sdk-cal-dot-color-1)); }",
      ".sdk-cal-day__dot--started:nth-child(3) { box-shadow: inset 0 0 0 var(--sdk-cal-started-border-width, 1px) var(--sdk-cal-started-border-color, var(--sdk-cal-dot-color-2)); }",
      ".sdk-cal-day__dot--started:nth-child(4) { box-shadow: inset 0 0 0 var(--sdk-cal-started-border-width, 1px) var(--sdk-cal-started-border-color, var(--sdk-cal-dot-color-3)); }",
      ".sdk-cal-day__dot--started:nth-child(5) { box-shadow: inset 0 0 0 var(--sdk-cal-started-border-width, 1px) var(--sdk-cal-started-border-color, var(--sdk-cal-dot-color-4)); }",
      /* Direct-child combinator (not descendant) — otherwise this also
         matches the .sdk-cal-day__dot spans nested inside .sdk-cal-day__dots
         two levels down, and its higher specificity (class+tag beats the
         dot's class-only rule) silently overrides the dot-size variable. */
      ".sdk-cal-day > a,",
      ".sdk-cal-day > span {",
      "  display: flex;",
      "  align-items: center;",
      "  justify-content: center;",
      "  width: 2.2rem;",
      "  height: 2.2rem;",
      "  margin: 2px auto;",
      "  border-radius: 50%;",
      "  font-family: var(--font-body--family);",
      "  font-size: 0.875rem;",
      "  line-height: 1;",
      "  box-sizing: border-box;",
      "}",
      ".sdk-cal-day a {",
      "  color: var(--color-foreground);",
      "  text-decoration: none;",
      "  border: 1px solid var(--color-foreground);",
      "}",
      ".sdk-cal-day a:hover {",
      "  background: var(--color-input-background);",
      "  border-color: var(--color-foreground);",
      "}",
      ".sdk-cal-day > span { opacity: 0.2; }",
      ".sdk-cal-day--active > span {",
      "  background: var(--color-border);",
      "  border: 1px solid var(--color-border);",
      "  font-weight: 700;",
      "  color: var(--color-primary-button-background);",
      "  opacity: 1;",
      "}",
    ].join("\n");
    document.head.appendChild(s);
  }

  var DIFFICULTY_COUNT = 5; // easy, medium, hard, extra-hard, diabolical — fixed left-to-right order

  /* Server-synced completions (customer.metafields.custom.sudoku_completions),
   * parsed once per buildCalendar() call from the container's data attribute.
   * Shape: { "2026-08-07": { "0": {...}, "2": {...} }, "stats": {...} } —
   * `stats` is skipped when reading, since it's not a date key. Stays null
   * for logged-out visitors and free-tier members (server never sends them
   * anything to begin with — see blocks/blog-archive-calendar.liquid). */
  var serverCompletions = null;

  /* Server-synced "started but unfinished" levels
   * (customer.metafields.custom.sudoku_in_progress.started), parsed from its
   * own data attribute alongside serverCompletions.
   * Shape: { "2026-07-08": "02" } — one character per begun level.
   *
   * Only the `started` half of that metafield is sent to the page: the
   * calendar has no use for the saved boards themselves, and shipping 20 full
   * grids into every page's HTML for a feature that needs five characters
   * would be waste. Deliberately a SEPARATE list from the boards on the server
   * too, so this marker never expires when a board does — see the Worker's
   * header for the full reasoning. */
  var serverStarted = null;

  /* Completion lookup — STAGE 2 (server + local union).
   *
   * Originally client-only (STAGE 1): read purely from the localStorage flag
   * the interactive puzzle grid writes (snippets/sudoku-carousel-grid.liquid,
   * function checkWin()) — key "sdkuwave-<date>-<levelIndex>" set to "1" the
   * first time that specific puzzle is fully solved. levelIndex 0-4 already
   * matches easiest-to-hardest, so no reordering is needed here.
   *
   * The puzzle grid stores its date with underscores ("2026_08_07") because
   * that format is also used to build its puzzle image asset filenames — the
   * calendar itself works in dashes ("2026-08-07"), so the conversion below is
   * the one deliberate place that difference gets bridged.
   *
   * Now unions that local flag with `serverCompletions` (paid members only —
   * see the shopify-app-proxy skill). Union, not replacement: a level solved
   * as a guest before logging in, or on a device that hasn't reconciled yet,
   * must not disappear just because the server doesn't have it too. This is
   * still the ONLY function that needed to change to add server sync —
   * buildDotsRow() and the render loop below are exactly as they were.
   */
  function getCompletedLevels(dateStr) {
    var underscoreDate = dateStr.split("-").join("_");
    var serverDay = serverCompletions && serverCompletions[dateStr];
    var done = [];
    for (var i = 0; i < DIFFICULTY_COUNT; i++) {
      var local =
        localStorage.getItem("sdkuwave-" + underscoreDate + "-" + i) === "1";
      var server = !!(serverDay && serverDay[String(i)]);
      done.push(local || server);
    }
    return done;
  }

  /* In-progress lookup — the exact structural mirror of getCompletedLevels()
   * above: same union of this browser's own storage with the server's copy,
   * same single seam, so the data source can change later without touching
   * any render code.
   *
   * Local test: the puzzle grid's saved board for that level has any player
   * content — at least one committed digit OR one pencil mark. Derived from the
   * keys the grid already writes (sdku- / sdkup-), no new key. Clearing the
   * board un-starts it, matching the rule the Worker applies server-side.
   *
   * Completion always outranks in-progress: a level reported as done is never
   * also reported as started, which is what makes a failed clear-on-win
   * harmless — a stale server entry simply stays invisible behind the
   * completion rather than showing both states at once. */
  function getStartedLevels(dateStr) {
    var underscoreDate = dateStr.split("-").join("_");
    var serverDay = serverStarted && serverStarted[dateStr];
    var completed = getCompletedLevels(dateStr);
    var started = [];
    for (var i = 0; i < DIFFICULTY_COUNT; i++) {
      if (completed[i]) {
        started.push(false);
        continue;
      }
      var board = localStorage.getItem("sdku-" + underscoreDate + "-" + i);
      var marks = localStorage.getItem("sdkup-" + underscoreDate + "-" + i);
      var local =
        (!!board && /[1-9]/.test(board)) || (!!marks && /[1-9]/.test(marks));
      var server =
        typeof serverDay === "string" && serverDay.indexOf(String(i)) !== -1;
      started.push(local || server);
    }
    return started;
  }

  /* Editor-only (see previewEditor above) — a made-up, but STABLE, pattern
     of done/started per level, keyed off the day-of-month number so the
     same date always draws the same way (no flicker/reshuffle on every
     redraw) while still varying across the visible month, showing every
     combination — done, started, and neither — for real style review.
     Reads nothing, writes nothing; the date string is only ever used for
     its trailing day-of-month digits. */
  function fakePreviewPattern(dateStr) {
    var day = parseInt(dateStr.slice(-2), 10) || 1;
    var completed = [];
    var started = [];
    for (var i = 0; i < DIFFICULTY_COUNT; i++) {
      var slot = (day + i) % 3;
      completed.push(slot === 0);
      started.push(slot === 1);
    }
    return { completed: completed, started: started };
  }

  /* dotsEnabled / startedEnabled are the two independent block toggles
     (show_completion_dots / show_started_dots). A finished level draws only
     when the first is on; an in-progress level only when the second is.
     previewEditor (editor-only, see its declaration in buildCalendar())
     swaps the two real per-day lookups below for a made-up display pattern —
     nothing else in this function changes. */
  function buildDotsRow(dateStr, dotsEnabled, startedEnabled, previewEditor) {
    var pattern = previewEditor ? fakePreviewPattern(dateStr) : null;
    var completed = pattern ? pattern.completed : getCompletedLevels(dateStr);
    var started = pattern ? pattern.started : getStartedLevels(dateStr);
    var row = document.createElement("div");
    row.className = "sdk-cal-day__dots";
    for (var i = 0; i < DIFFICULTY_COUNT; i++) {
      var dot = document.createElement("span");
      var state = "";
      if (completed[i] && dotsEnabled) state = " sdk-cal-day__dot--done";
      else if (started[i] && startedEnabled)
        state = " sdk-cal-day__dot--started";
      dot.className = "sdk-cal-day__dot" + state;
      row.appendChild(dot);
    }
    return row;
  }

  function buildCalendar(container) {
    var firstDow = container.dataset.firstDow === "monday" ? 1 : 0;
    var chevronStroke = container.dataset.chevronStroke || "1.5";
    var activeDate = container.dataset.activeDate || "";
    var blogHandle = container.dataset.blogHandle || "";
    var token = container.dataset.storefrontToken || "";
    var isMember = container.dataset.isMember === "true";
    var gateUrl = container.dataset.gateUrl || "";
    var gateEnabled = container.dataset.gateEnabled === "true";
    var dotsEnabled = container.dataset.dotsEnabled === "true";
    var startedEnabled = container.dataset.startedEnabled === "true";
    var blockId = container.dataset.blockId || "";
    /* Editor-only marker style preview (2026-09-14) — double-gated so it can
     * never show for a real visitor: the data attribute only exists at all
     * when Liquid rendered inside {% if request.design_mode %} (see
     * blocks/blog-archive-calendar-alert.liquid), AND this also checks the
     * live Shopify.designMode flag itself. Either gate alone would be
     * enough; both together means a bug in one doesn't leak this live. When
     * on, buildDotsRow() below draws a deterministic made-up pattern of
     * done/started markers instead of this browser's/customer's real
     * progress — pure display, no read or write of any real completion
     * data, so there is nothing here that could affect a real account. */
    var previewEditor =
      container.dataset.previewDotsEditor === "true" &&
      !!(window.Shopify && window.Shopify.designMode);
    if (!token) return;

    /* Parsed once per instance — server-rendered by Liquid, no extra fetch
     * needed (see blocks/blog-archive-calendar.liquid). Absent/invalid for
     * logged-out visitors and free-tier members; getCompletedLevels() treats
     * null the same as "nothing from the server", falling back to local only. */
    try {
      var raw = container.dataset.serverCompletions;
      var parsed = raw ? JSON.parse(raw) : null;
      serverCompletions = parsed && typeof parsed === "object" ? parsed : null;
    } catch (e) {
      serverCompletions = null;
    }

    /* Same treatment for the in-progress list — parsed separately so a problem
     * with one never costs the other. */
    try {
      var rawStarted = container.dataset.serverStarted;
      var parsedStarted = rawStarted ? JSON.parse(rawStarted) : null;
      serverStarted =
        parsedStarted && typeof parsedStarted === "object"
          ? parsedStarted
          : null;
    } catch (e) {
      serverStarted = null;
    }

    /* Today in Vienna time — articles publish at midnight Vienna time.
     * Computed up front so the archive cache can be invalidated once this rolls
     * over, instead of trusting a sessionStorage entry from a prior day. */
    var todayStr = new Date().toLocaleDateString("sv-SE", {
      timeZone: "Europe/Vienna",
    });

    fetchArchive(token, blogHandle, todayStr, function (rawData) {
      /* Build date-string → pathname lookup. */
      var map = Object.create(null);
      for (var i = 0; i < rawData.length; i++) {
        map[rawData[i][0]] = rawData[i][1];
      }

      /* ── Public day-navigation API ────────────────────────────────────────
       * Published for the interactive puzzle grid (snippets/
       * sudoku-carousel-grid.liquid), whose date-swipe feature has to answer
       * "which day should this go to?" — which needs exactly the two things
       * this calendar already owns: which days exist, and which levels are
       * already done.
       *
       * getCompletedLevels is EXPOSED, never copied: it is the single
       * definition of "completed" (unioning this device's localStorage with
       * the server-synced metafield), and a second implementation in the grid
       * would inevitably drift out of step with this one.
       *
       * Dates here are Vienna-local calendar dates ("YYYY-MM-DD"), already
       * converted from Shopify's UTC publishedAt inside fetchArchive() —
       * consumers must never re-derive a date from a raw timestamp
       * themselves. datesNewestFirst is safe to compare and sort as plain
       * strings: zero-padded ISO dates order chronologically, the same
       * assumption the `ds < todayStr` gating check further down already
       * relies on.
       *
       * Multiple calendar instances can exist on one page (the daily article
       * template has two). They share one archive, so last-write-wins is
       * harmless — but a failed/empty fetch must never clobber a good archive,
       * hence the length guard. */
      if (!window.sdkArchiveAPI || rawData.length) {
        window.sdkArchiveAPI = {
          today: todayStr,
          datesNewestFirst: rawData.map(function (r) {
            return r[0];
          }),
          urlForDate: function (dateStr) {
            return map[dateStr] || null;
          },
          getCompletedLevels: getCompletedLevels,
          difficultyCount: DIFFICULTY_COUNT,
        };
        window.dispatchEvent(new CustomEvent("sdk:archive-ready"));
      }

      /* Live redraw hook — called by the "sdk:puzzle-completed" listener below.
       * Only touches the one day cell (if it's in the currently displayed
       * month), so a completion doesn't force a full calendar re-render. */
      container.sdkCalRefreshDate = function (dateStr) {
        if ((!dotsEnabled && !startedEnabled) || !map[dateStr]) return;
        var cell = container.querySelector(
          '.sdk-cal-day[data-date="' + dateStr + '"]',
        );
        if (!cell) return;
        var oldDots = cell.querySelector(".sdk-cal-day__dots");
        if (oldDots) oldDots.remove();
        cell.insertBefore(
          buildDotsRow(dateStr, dotsEnabled, startedEnabled, previewEditor),
          cell.firstChild,
        );
      };

      var todayParts = todayStr.split("-");
      var curYear = parseInt(todayParts[0], 10);
      var curMonth = parseInt(todayParts[1], 10) - 1; // 0-indexed

      /* Earliest month — last item in newest-first array. Fall back to current month
       * if archive is empty (token set but no articles yet). */
      var earlyYear, earlyMonth;
      if (rawData.length) {
        var parts = rawData[rawData.length - 1][0].split("-");
        earlyYear = parseInt(parts[0], 10);
        earlyMonth = parseInt(parts[1], 10) - 1;
      } else {
        earlyYear = curYear;
        earlyMonth = curMonth;
      }

      var viewYear, viewMonth;
      if (activeDate) {
        var activeParts = activeDate.split("-");
        viewYear = parseInt(activeParts[0], 10);
        viewMonth = parseInt(activeParts[1], 10) - 1;
      } else {
        viewYear = curYear;
        viewMonth = curMonth;
      }

      function render() {
        container.innerHTML = "";

        var prevDisabled =
          viewYear < earlyYear ||
          (viewYear === earlyYear && viewMonth <= earlyMonth);
        var nextDisabled =
          viewYear > curYear || (viewYear === curYear && viewMonth >= curMonth);

        /* Navigation row */
        var nav = document.createElement("div");
        nav.className = "sdk-cal-nav";

        var prevBtn = document.createElement("button");
        prevBtn.className = "sdk-cal-nav__btn";
        prevBtn.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 8 14" fill="none" aria-hidden="true"><path d="M7 1L1 7L7 13" stroke="currentColor" stroke-width="' +
          chevronStroke +
          '" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        prevBtn.setAttribute("aria-label", "Previous month");
        if (prevDisabled) prevBtn.setAttribute("disabled", "");
        prevBtn.addEventListener("click", function () {
          if (viewMonth === 0) {
            viewYear--;
            viewMonth = 11;
          } else {
            viewMonth--;
          }
          render();
        });

        var monthLabel = document.createElement("span");
        monthLabel.className = "sdk-cal-nav__label";
        monthLabel.textContent = MONTHS[viewMonth] + " " + viewYear;

        var nextBtn = document.createElement("button");
        nextBtn.className = "sdk-cal-nav__btn";
        nextBtn.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 8 14" fill="none" aria-hidden="true"><path d="M1 1L7 7L1 13" stroke="currentColor" stroke-width="' +
          chevronStroke +
          '" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        nextBtn.setAttribute("aria-label", "Next month");
        if (nextDisabled) nextBtn.setAttribute("disabled", "");
        nextBtn.addEventListener("click", function () {
          if (viewMonth === 11) {
            viewYear++;
            viewMonth = 0;
          } else {
            viewMonth++;
          }
          render();
        });

        nav.appendChild(prevBtn);
        nav.appendChild(monthLabel);
        nav.appendChild(nextBtn);
        container.appendChild(nav);

        /* Day grid */
        var grid = document.createElement("div");
        grid.className = "sdk-cal-grid";

        var labels = firstDow === 1 ? DOW_MON : DOW_SUN;
        for (var d = 0; d < 7; d++) {
          var hdr = document.createElement("div");
          hdr.className = "sdk-cal-dow";
          hdr.textContent = labels[d];
          grid.appendChild(hdr);
        }

        /* Offset before day 1. */
        var firstDay = new Date(viewYear, viewMonth, 1).getDay();
        var offset =
          firstDow === 1 ? (firstDay === 0 ? 6 : firstDay - 1) : firstDay;
        var daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

        for (var p = 0; p < offset; p++) {
          grid.appendChild(document.createElement("div"));
        }

        for (var day = 1; day <= daysInMonth; day++) {
          var ds = toDateStr(viewYear, viewMonth, day);
          var cell = document.createElement("div");
          cell.className = "sdk-cal-day";
          cell.dataset.date = ds;
          if (ds === todayStr) cell.classList.add("sdk-cal-day--today");
          if (activeDate && ds === activeDate)
            cell.classList.add("sdk-cal-day--active");

          var url = map[ds];
          var isActive = activeDate && ds === activeDate;
          if ((dotsEnabled || startedEnabled) && url) {
            cell.appendChild(
              buildDotsRow(ds, dotsEnabled, startedEnabled, previewEditor),
            );
          }
          if (url && !isActive) {
            var gated = gateEnabled && ds < todayStr && !isMember && gateUrl;
            if (gated) cell.classList.add("sdk-cal-day--gated");
            var a = document.createElement("a");
            a.href = gated ? gateUrl : url;
            a.textContent = day;
            if (gated) {
              a.addEventListener("click", function (e) {
                var dlg = document.getElementById("gate-dialog-" + blockId);
                if (dlg && dlg.showDialog) {
                  e.preventDefault();
                  dlg.showDialog();
                }
                /* else: falls through to the normal href navigation
                   (JS-off / component-missing / popup-disabled fallback) */
              });
            } else if (container.dataset.softSwap === "true") {
              /* In-puzzle alert only. Ask the fullscreen puzzle to soft-swap
                 to this day instead of navigating. The event is cancelable:
                 dispatchEvent returns false only if a listener called
                 preventDefault() (i.e. it took over), and only then is the
                 link suppressed — so with no interactive puzzle present the
                 <a> still navigates normally. date/url are read off the DOM
                 at click time, not captured, to dodge the loop-var closure. */
              a.addEventListener("click", function (e) {
                var cellEl = this.closest(".sdk-cal-day");
                var pick = new CustomEvent("sdk:calendar-day-pick", {
                  cancelable: true,
                  detail: {
                    date: cellEl ? cellEl.dataset.date : null,
                    url: this.href,
                  },
                });
                if (!document.dispatchEvent(pick)) e.preventDefault();
              });
            }
            cell.appendChild(a);
          } else {
            var span = document.createElement("span");
            span.textContent = day;
            cell.appendChild(span);
          }
          grid.appendChild(cell);
        }

        container.appendChild(grid);
      }

      /* Public: jump the visible month to the one containing dateStr and mark
       * that day as the active day. The in-puzzle archive-calendar alert calls
       * this each time it opens, so it shows whatever day the fullscreen puzzle
       * is currently on (which changes via soft-swap, with no page reload)
       * rather than the day the page was first loaded. dateStr is a Vienna
       * "YYYY-MM-DD" — the same form as data-active-date and the archive keys. */
      container.sdkCalGoToDate = function (dateStr) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr || "")) return;
        var p = dateStr.split("-");
        activeDate = dateStr;
        viewYear = parseInt(p[0], 10);
        viewMonth = parseInt(p[1], 10) - 1;
        render();
      };

      render();
    });
  }

  /* Fired by sudoku-carousel-grid.liquid the instant a puzzle is solved
   * (see checkWin() there) — lets any calendar on the same page update its
   * dot for that day immediately, with no page refresh. Puzzle grid dates
   * are stored with underscores ("2026_08_07"); the calendar works in
   * dashes, so this is the bridge point (mirrors getCompletedLevels above). */
  /* Completion is permanent (monotonic) — the frozen serverCompletions blob
     parsed once at page load can only ever be MISSING a true, never wrongly
     claim a false one, so no patch is needed for sdk:puzzle-completed.
     "Started" is NOT monotonic (Clear flips it back to false), so a
     sdk:puzzle-progress event patches serverStarted for this one date/level
     — otherwise a stale "was started at page load" would keep out-voting a
     fresh "no longer started" in the local||server union forever, within
     this same page view. Mirrors the Worker's own addStarted/removeStarted
     string shape exactly. */
  function patchServerStarted(dateStr, level, isStarted) {
    if (!serverStarted) serverStarted = {};
    var cur =
      typeof serverStarted[dateStr] === "string" ? serverStarted[dateStr] : "";
    var has = cur.indexOf(String(level)) !== -1;
    if (isStarted && !has) {
      serverStarted[dateStr] = (cur + String(level)).split("").sort().join("");
    } else if (!isStarted && has) {
      var next = cur
        .split("")
        .filter(function (c) {
          return c !== String(level);
        })
        .join("");
      if (next) serverStarted[dateStr] = next;
      else delete serverStarted[dateStr];
    }
  }

  function onPuzzleCompleted(e) {
    var rawDate = (e.detail && e.detail.date) || "";
    if (!rawDate) return;
    var dateStr = rawDate.split("_").join("-");
    var level = e.detail && e.detail.level;
    if (typeof level === "number" && typeof e.detail.started === "boolean") {
      patchServerStarted(dateStr, level, e.detail.started);
    }
    document.querySelectorAll(".sdk-cal").forEach(function (el) {
      if (typeof el.sdkCalRefreshDate === "function") {
        el.sdkCalRefreshDate(dateStr);
      }
    });
  }

  function init() {
    injectStyles();
    document.querySelectorAll(".sdk-cal").forEach(function (el) {
      buildCalendar(el);
    });
    window.addEventListener("sdk:puzzle-completed", onPuzzleCompleted);
    /* Same redraw, different trigger: a puzzle on this page gaining or losing
       its first digit flips the in-progress marker, and the calendar should
       show that immediately rather than at the next page load. Both events
       carry { date, level }, so one handler serves both. */
    window.addEventListener("sdk:puzzle-progress", onPuzzleCompleted);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
